import { observer } from "mobx-react-lite";
import { useFormikContext } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";

import { useTran } from "@/hooks";
import { SearchableSelect } from "@/components/common";
import FileUploadDropzone from "@/components/common/FileUploadDropzone";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { reportingBoundaryService } from "@/features/reporting-boundary/services/reporting-boundary.services";
import { dashboardService } from "@/features/esg-dashboard-v3/services/dashboard.service";
import { sortReportingYearsDescending } from "@/features/esg-dashboard-v3/utils/reporting-years";
import { formatReportingYearPeriod } from "@/lib/utils/period-constants";
import { DecimalSeparator } from "@/lib/utils/number";

const TAB_TRIGGER_CLASSNAME =
  "rounded-full px-4 py-1.5 data-[state=active]:bg-primary dark:data-[state=active]:bg-primary data-[state=active]:text-primary-foreground";

const SCOPE_FIELDS = [
  { key: "ghgBaselineScope1", label: "Scope 1 (tCO₂e)" },
  { key: "ghgBaselineScope2", label: "Scope 2 (tCO₂e)" },
  { key: "ghgBaselineScope3", label: "Scope 3 (tCO₂e)" },
];

const EMPTY_SCOPE_STATE = {
  loading: false,
  error: false,
  scope1: null,
  scope2: null,
};

const formatScopeValue = (value) =>
  typeof value === "number" && Number.isFinite(value)
    ? `${DecimalSeparator(value, 2)} tCO₂e`
    : null;

const CompanySetupStepFive = observer(() => {
  const { t, i18n } = useTran();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const mode = values.ghgBaselineMode || "reportingYear";

  const [reportingYearRows, setReportingYearRows] = useState([]);
  const [yearsLoading, setYearsLoading] = useState(false);
  const [yearsError, setYearsError] = useState(false);
  const hasFetchedYearsRef = useRef(false);

  useEffect(() => {
    // Only fetch once the reporting-year tab is actually viewed — a company
    // saved in "custom" mode shouldn't pay for this request on every visit.
    if (mode !== "reportingYear" || hasFetchedYearsRef.current) return;

    let isMounted = true;
    setYearsLoading(true);
    setYearsError(false);

    reportingBoundaryService
      .getReportingYearByCompany()
      .then((data) => {
        if (!isMounted) return;
        setReportingYearRows(
          Array.isArray(data?.reportingYears) ? data.reportingYears : [],
        );
      })
      .catch(() => {
        if (!isMounted) return;
        setYearsError(true);
      })
      .finally(() => {
        // Only mark "fetched" once a real (non-cancelled) attempt completes —
        // React StrictMode's throwaway dev double-invoke cancels the first
        // attempt via isMounted before it resolves, so marking the ref
        // synchronously up front would permanently block the real retry.
        if (!isMounted) return;
        hasFetchedYearsRef.current = true;
        setYearsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [mode]);

  const reportingYearOptions = useMemo(() => {
    const seenYears = new Set();
    return sortReportingYearsDescending(reportingYearRows)
      .filter((row) => {
        if (!row?.startYear || seenYears.has(row.startYear)) return false;
        seenYears.add(row.startYear);
        return true;
      })
      .map((row) => ({
        value: String(row.startYear),
        label: formatReportingYearPeriod(row, i18n.language),
      }));
  }, [reportingYearRows, i18n.language]);

  const selectedRow = useMemo(
    () =>
      reportingYearRows.find(
        (row) => String(row?.startYear) === values.ghgBaselineReportingYear,
      ) || null,
    [reportingYearRows, values.ghgBaselineReportingYear],
  );

  const [scopeState, setScopeState] = useState(EMPTY_SCOPE_STATE);

  useEffect(() => {
    if (mode !== "reportingYear" || !selectedRow) {
      setScopeState(EMPTY_SCOPE_STATE);
      return;
    }

    let isMounted = true;
    setScopeState((prev) => ({ ...prev, loading: true, error: false }));

    dashboardService
      .getEChartsTotals({
        frameworks: selectedRow.frameworkCodes || [],
        years: [selectedRow.startYear],
        startYear: selectedRow.startYear,
        startMonth: selectedRow.startMonth,
        endYear: selectedRow.endYear,
        endMonth: selectedRow.endMonth,
      })
      .then((data) => {
        if (!isMounted) return;
        const summaryForYear = data?.summary?.[String(selectedRow.startYear)];
        setScopeState({
          loading: false,
          error: false,
          scope1: summaryForYear?.scope1 ?? null,
          scope2: summaryForYear?.scope2 ?? null,
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setScopeState({ ...EMPTY_SCOPE_STATE, error: true });
      });

    return () => {
      isMounted = false;
    };
  }, [mode, selectedRow]);

  const handleModeChange = (nextMode) => {
    if (nextMode === mode) return;
    setFieldValue("ghgBaselineMode", nextMode, false);
    setFieldTouched("ghgBaselineMode", true, false);
  };

  const setField = (key, value) => {
    setFieldValue(key, value, false);
    setFieldTouched(key, true, false);
  };

  const handleReportingYearChange = (nextValue) => {
    const row = reportingYearRows.find(
      (candidate) => String(candidate?.startYear) === nextValue,
    );
    // `mode` above defaults an unset ghgBaselineMode ("" — never configured)
    // to "reportingYear" for display, so this tab can already look active
    // without the user ever clicking its trigger — which is the only thing
    // that would otherwise set ghgBaselineMode via handleModeChange (Tabs'
    // onValueChange only fires on an actual value change). Without this,
    // picking a year the first time leaves ghgBaselineMode stuck at "",
    // and company.store.js's payload builder only sends the reportingYear
    // fields when it's exactly "reportingYear" — so nothing saves until the
    // user happens to toggle to Custom and back, which sets it for real.
    setField("ghgBaselineMode", "reportingYear");
    setField("ghgBaselineReportingYear", nextValue);
    setField("ghgBaselineReportingBoundaryId", row?._id || "");
    setField("ghgBaselineStartMonth", row?.startMonth ?? "");
    setField("ghgBaselineEndYear", row?.endYear ?? "");
    setField("ghgBaselineEndMonth", row?.endMonth ?? "");
  };

  return (
    <Tabs value={mode} onValueChange={handleModeChange} className="space-y-6">
      <TabsList className="rounded-full border-none bg-muted/40 p-1">
        <TabsTrigger value="reportingYear" className={TAB_TRIGGER_CLASSNAME}>
          {t("select_reporting_year")}
        </TabsTrigger>
        <TabsTrigger value="custom" className={TAB_TRIGGER_CLASSNAME}>
          {t("custom")}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="custom" className="mt-0 space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {SCOPE_FIELDS.map((field) => (
            <div key={field.key} className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {field.label}
              </label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={values[field.key] || ""}
                onChange={(event) => setField(field.key, event.target.value)}
              />
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground">
              {t("attach_evidence")}
            </label>
            <Badge variant="outline" className="font-normal">
              {t("optional")}
            </Badge>
          </div>
          <FileUploadDropzone
            value={values.ghgBaselineEvidence}
            onChange={(nextValue) => setField("ghgBaselineEvidence", nextValue)}
            accept=".pdf,.xlsx,.docx,.jpg,.jpeg,.png"
            frameClassName="min-h-0 gap-2 p-4"
            attachedText={t("evidence_file_attached")}
          />
        </div>
      </TabsContent>

      <TabsContent value="reportingYear" className="mt-0 space-y-2">
        <label className="text-sm font-medium text-foreground">
          {t("baseline_reporting_year")}
        </label>

        {yearsLoading ? (
          <p className="text-xs text-muted-foreground">
            {t("loading_reporting_years")}
          </p>
        ) : reportingYearOptions.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            {t("no_reporting_years_found")}
          </p>
        ) : (
          <>
            <SearchableSelect
              value={values.ghgBaselineReportingYear}
              onChange={handleReportingYearChange}
              options={reportingYearOptions}
              placeholder={t("select_reporting_year")}
              lockSingleOption={false}
            />
            <p className="text-xs text-muted-foreground">
              {t("ghg_baseline_hint")}
            </p>
          </>
        )}

        {yearsError ? (
          <p className="text-xs text-destructive">
            {t("unable_load_reporting_years")}
          </p>
        ) : null}

        {selectedRow ? (
          <div className="grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2">
            {[
              { label: "Scope 1", value: scopeState.scope1 },
              { label: "Scope 2", value: scopeState.scope2 },
            ].map((scope) => (
              <div
                key={scope.label}
                className="rounded-radius border border-border p-3"
              >
                <p className="text-xs text-muted-foreground">{scope.label}</p>
                <p className="mt-1 text-sm font-semibold text-foreground">
                  {scopeState.loading
                    ? t("Loading...")
                    : scopeState.error
                      ? t("unable_to_load")
                      : formatScopeValue(scope.value) || t("no_data")}
                </p>
              </div>
            ))}
          </div>
        ) : null}
      </TabsContent>
    </Tabs>
  );
});

export default CompanySetupStepFive;
